import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { User, AuthState } from '../types';

// Action types
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_LOADING'; payload: boolean };

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Context type
interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; redirect?: string; error?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      console.log('Checking auth status...');
      const response = await authAPI.checkAuth();
      console.log('Auth check response:', response);
      
      if (response.authenticated && response.user) {
        console.log('User authenticated:', response.user.username, response.user.role);
        dispatch({ type: 'SET_USER', payload: response.user });
      } else {
        console.log('User not authenticated');
        dispatch({ type: 'SET_USER', payload: null });
      }
    } catch (error) {
      console.error('Auth check error:', error);
      dispatch({ type: 'SET_USER', payload: null });
    }
  };

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authAPI.login(email, password);
      
      if (response.success && response.user) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.user });
        
        console.log('Login response received:', { success: response.success, hasUser: !!response.user, hasToken: !!response.token });
        
        // Store JWT token for cookie-restricted environments
        if (response.token) {
          localStorage.setItem('auth_token', response.token);
          console.log('JWT token stored for cookie-restricted environment');
          console.log('Token length:', response.token.length);
          console.log('Stored token verification:', localStorage.getItem('auth_token') ? 'success' : 'failed');
        } else {
          console.warn('No token in login response');
        }
        
        // Edge compatibility: Wait and verify session was created
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Double-check authentication status for Edge browser compatibility
        const authCheck = await authAPI.checkAuth();
        console.log('First auth check:', authCheck);
        if (!authCheck.authenticated) {
          console.warn('Session not persisted, trying JWT token fallback...');
          
          // Ensure token is available for next request
          if (response.token) {
            console.log('Token available, forcing axios interceptor refresh...');
            // Force axios to pick up the new token by creating a new request
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            const retryCheck = await authAPI.checkAuth();
            console.log('Retry auth check:', retryCheck);
            if (retryCheck.authenticated) {
              console.log(`Authentication successful via ${retryCheck.auth_method || 'token'}`);
            } else {
              console.error('Both session and token authentication failed');
              console.log('Token in localStorage:', localStorage.getItem('auth_token') ? 'present' : 'missing');
              // Clear stored token if it doesn't work
              localStorage.removeItem('auth_token');
            }
          } else {
            console.error('No token received from server');
          }
        } else {
          console.log(`Authentication successful via ${authCheck.auth_method || 'session'}`);
        }
        
        return { success: true, redirect: response.redirect };
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: response.error || 'Login failed' });
        return { success: false, error: response.error };
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'An error occurred during login';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      console.log('Starting logout process...');
      // Always clear local state first to prevent UI errors
      dispatch({ type: 'LOGOUT' });
      localStorage.removeItem('auth_token');
      
      // Then try to logout from server
      await authAPI.logout();
      console.log('Server logout successful');
    } catch (error) {
      console.error('Server logout error:', error);
      // Local state is already cleared, so this is fine
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
